<template>
  <div>
    <div v-if="budgetPercent > 0" class="chart__container">
      <PieProgress class="chart" :percent="budgetPercent" halfCircle />

      <div class="chart__calc">
        <span class="chart__value">
          <strong>{{ $t(`${namespace}.balance`) }}</strong> {{ balanceAmount | currencyBRL }}
        </span>

        <span class="chart__value">
          <strong>{{ $t(`${namespace}.consumed`) }}</strong> {{ data.usedBudget | currencyBRL }}
        </span>
      </div>
    </div>

    <div class="mb-2">
      <div class="item__label">{{ $t(`${namespace}.budget`) }}</div>
      <div>{{ data.budgetMonthlyLimit | currencyBRL }}</div>
    </div>

    <div class="mb-2">
      <div class="item__label">{{ $t(`${namespace}.costCenterManager`) }}</div>

      <div v-if="data.ownerName">{{ data.ownerName }}</div>

      <div v-if="isValidArray(data.owners)" class="text--ellipsis">
        <div v-for="(item, index) in data.owners" :key="index">
          {{ item.name }}
        </div>
      </div>

      <div v-if="!data.ownerName && !isValidArray(data.owners) && claims.costCenter['ROLE_PUT_COSTCENTER_ID']">
        <router-link
          :to="{
            name: 'CostCenter',
            params: { type: 'editar', id: data.id }
          }"
          class="accent--text"
        >
          {{ $t(`${namespace}.setOwner`) }}
        </router-link>
      </div>

      <span v-if="!data.ownerName && !isValidArray(data.owners) && !claims.costCenter['ROLE_PUT_COSTCENTER_ID']">
        N/A
      </span>
    </div>

    <div class="mb-2">
      <div class="item__label">{{ $t(`${namespace}.extraBudget`) }}</div>

      <div v-if="data.extraMonthlyBudget || !claims.costCenter['ROLE_PUT_COSTCENTER_ID']">
        {{ data.extraMonthlyBudget | currencyBRL }}
      </div>

      <router-link
        v-if="!data.extraMonthlyBudget && claims.costCenter['ROLE_PUT_COSTCENTER_ID']"
        :to="{
          name: 'CostCenter',
          params: { type: 'editar', id: data.id }
        }"
        class="accent--text"
      >
        {{ $t(`${namespace}.setExtraBudget`) }}
      </router-link>
    </div>

    <div v-if="claims.costCenter['ROLE_PUT_COSTCENTER_ID']" class="mb-3">
      <v-layout align-center justify-center row fill-height>
        <v-flex xs11>
          <span class="caption gray-6--text">
            {{ $t(`${namespace}.temporaryBlock`) }}
          </span>
        </v-flex>

        <v-flex xs1>
          <v-switch
            v-model="temporaryDisabled"
            color="success"
            class="ma-0"
            hide-details
            @change="handleTemporaryDisabled"
          />
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isObject, isArray } from 'utils/functions'
import PieProgress from 'components/PieProgress'

export default {
  name: 'TabInfo',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  components: {
    PieProgress
  },
  computed: {
    ...mapState({
      claims: ({ userClaims }) => userClaims.claims,
      costCenterTemporaryDisabled: ({ globalDrawer }) => globalDrawer.costCenterTemporaryDisabled
    }),

    totalBudget () {
      const budgetMonthlyLimit = this.data.budgetMonthlyLimit ? this.data.budgetMonthlyLimit : 0
      const extraMonthlyBudget = this.data.extraMonthlyBudget ? this.data.extraMonthlyBudget : 0
      return budgetMonthlyLimit + extraMonthlyBudget
    },

    balanceAmount () {
      const usedBudget = this.data.usedBudget ? this.data.usedBudget : 0
      return this.totalBudget - usedBudget
    },

    budgetPercent () {
      const usedBudget = this.data.usedBudget ? this.data.usedBudget : 0
      return this.totalBudget ? (usedBudget / this.totalBudget) * 100 : 0
    }
  },
  data () {
    return {
      namespace: 'Components.GlobalDrawer.costCenter.tabs',
      temporaryDisabled: false
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show',
      setCostCenterTemporaryDisabled: 'globalDrawer/set'
    }),

    isValidObject (val) {
      return isObject(val)
    },

    isValidArray (val) {
      return isArray(val)
    },

    async handleTemporaryDisabled (val = false) {
      try {
        await this.$apiService.updateCostCenter({
          ...this.data,
          temporaryDisabled: val
        })

        this.showAlert({
          type: 'success',
          message: val
            ? this.$t(`Components.CostCenterForm.disableCostCenterMonthSuccess`)
            : this.$t(`Components.CostCenterForm.enabledCostCenterMonthSuccess`)
        })

        this.setCostCenterTemporaryDisabled({
          costCenterTemporaryDisabled: val
        })
      } catch (err) {
        console.log('ERROR:', err)

        this.temporaryDisabled = this.temporaryDisabledOld

        this.setCostCenterTemporaryDisabled({
          costCenterTemporaryDisabled: this.temporaryDisabledOld
        })

        if (err.response.data && err.response.data.errors) {
          const key = Object.keys(err.response.data.errors)[0]
          let msg

          if (key === 'name') {
            msg = this.$t(`Components.CostCenterForm.costCenteRequiredMessage`)
            return this.showAlert({ type: 'error', message: msg })
          }

          if (key === 'companyId') {
            msg = this.$t(`Components.CostCenterForm.selectedACompany`)
            return this.showAlert({ type: 'error', message: msg })
          }

          this.showAlert({
            type: 'error',
            message: this.temporaryDisabled
              ? this.$t(`Components.CostCenterForm.enabledCostCenterMonthError`)
              : this.$t(`Components.CostCenterForm.disableCostCenterMonthError`)
          })
        }

        this.showAlert({
          type: 'error',
          message: this.temporaryDisabled
            ? this.$t(`Components.CostCenterForm.disableCostCenterMonthError`)
            : this.$t(`Components.CostCenterForm.enabledCostCenterMonthError`)
        })
      }
    }
  },
  mounted () {
    if (this.isValidObject(this.data)) {
      this.temporaryDisabled = this.data.temporaryDisabled
      this.temporaryDisabledOld = this.data.temporaryDisabled
    }
  },
  watch: {
    costCenterTemporaryDisabled (val) {
      this.temporaryDisabled = val
      this.temporaryDisabledOld = val
    }
  }
}
</script>

<style lang="scss" scoped>
.chart__container {
  margin-bottom: 12px;
  text-align: center;
}

.chart {
  margin: 0 auto;
}

.chart__calc {
  position: relative;
  top: -10px;
}

.chart__value {
  display: block;
  font-size: 12px;
}
</style>
